import { CashRegister, CashRegisterClosing, CashRegisterOpening } from '@shared/models/cash-register';
import { CashRegisterClosingV3, CashRegisterOpeningV3, CashRegisterV3 } from 'src/app/point-of-sale/models/cash-register-v3';

/** Both POS generations share one Native Service store and one Firestore document shape. */
export type CashRegisterEntity = CashRegister | CashRegisterV3;
export type CashRegisterOpeningDetails = CashRegisterOpening | CashRegisterOpeningV3;
export type CashRegisterClosingDetails = CashRegisterClosing | CashRegisterClosingV3;

/** Every cash register mutation the application performs. */
export type CashRegisterOperation =
  | 'create'
  | 'update'
  | 'open'
  | 'close'
  | 'applyServerConfiguration'
  | 'unpair';

/**
 * Which part of the document a change touches. Each scope carries its own
 * ordering stamp so a configuration pull can never be blocked by (or clobber)
 * a concurrent open/close and vice versa.
 */
export type CashRegisterChangeScope = 'state' | 'configuration';

/**
 * A state change the store computed and wrote, carried back to the caller so
 * the facade can record exactly one terminal Seq event per write.
 */
export interface CashRegisterStateChange {
  cashRegisterId: string;
  operation: CashRegisterOperation;
  scope: CashRegisterChangeScope;
  /**
   * Ordering stamp of the change: the domain timestamp for open/close
   * (`opening_date` / `closing_date`), otherwise the time of the write. Each
   * scope carries its own stamp.
   */
  occurredAt: Date;
  /** Top-level entity fields the operation replaced (the native merge is top-level). */
  changedFields: string[];
  /**
   * The complete entity as it now exists. Used in full when the document does
   * not exist yet, otherwise only {@link changedFields} are taken from it.
   */
  state: CashRegisterEntity;
  /** Correlates the primary write, the replica write and the Seq events of one workflow. */
  correlationId: string;
  /** Which store produced the state — `Firestore`, the source of truth. */
  source: string;
  /** Human-readable description of the primary operation, e.g. `PUT /cashRegisters/open/{id}`. */
  primaryOperation: string;
}

export type CashRegisterSyncOutcome =
  | {
    /** The change sits in the durable local queue; `acknowledged` settles when the backend accepted or refused it. */
    status: 'applied';
    created: boolean;
    acknowledged: Promise<void>;
  }
  | { status: 'skipped'; reason: 'stale' | 'nothingToSync'; existingStamp?: Date }
  | { status: 'failed'; error: unknown };

/**
 * Result of an application-level cash register operation.
 *
 * `cashRegister` is the resulting state and is what callers act on.
 * `firestoreSync` tracks the durable write; it never rejects — a failure is
 * logged to Seq and reported as `{ status: 'failed' }`, so callers may await it
 * or ignore it.
 */
export interface CashRegisterOperationResult<T extends CashRegisterEntity = CashRegisterEntity> {
  cashRegister: T;
  firestoreSync: Promise<CashRegisterSyncOutcome>;
  /** What the store wrote, carried so the facade can log one terminal Seq event per write. */
  change?: CashRegisterStateChange;
}

/**
 * Concurrency / provenance block stored on the Firestore document next to
 * the entity fields. Per-scope stamps implement "newest state wins" without a
 * new mechanism: they are the same domain timestamps the entity already
 * carries (`opening_date`, `closing_date`) or the primary acknowledgement time.
 */
export interface CashRegisterRevision {
  stateChangedAt?: Date;
  configurationChangedAt?: Date;
  lastOperation?: CashRegisterOperation;
  /**
   * Where the last write originated — `Firestore` for a write made by the
   * store, `ServiceWorker` for the configuration/unpair reconcile the pull
   * service worker performs when no window is open.
   */
  source?: string;
  correlationId?: string;
}

/**
 * The contract every cash register store honours, implemented by
 * `CashRegisterReplicationService` and selected through the
 * `CASH_REGISTER_STORE` token (see `cash-register-store.token.ts`), so
 * consumers never depend on which store is configured.
 */
export interface CashRegisterStore {
  /** The stored register, or `undefined` when it is absent or cannot be read. Never throws. */
  getByIdAsync(cashRegisterId: string): Promise<CashRegisterEntity | undefined>;
  /**
   * The register assigned to a user, regardless of whether it is active — the
   * Desktop's `ByEmail` view ignored `active` and let the caller decide. Never
   * throws.
   */
  findByActivatedUserAsync(email: string): Promise<CashRegisterEntity | undefined>;
  /**
   * Open a register. Resolves with the resulting register state; rejects when
   * the guards refuse the transition. A register that is not stored yet is
   * created from `cashRegister` — the server's record, the only copy a device
   * holds before its first write — so it can still be opened, and a fragment of
   * one (a document holding only state, left by a merge that landed after a
   * deletion) is completed from it; once stored whole, the stored state alone
   * decides and the copy is ignored.
   */
  openAsync(cashRegister: CashRegisterEntity, opening: CashRegisterOpeningDetails): Promise<CashRegisterOperationResult>;
  /**
   * Close a register. Same contract as {@link openAsync}: a register that is
   * not stored yet is created from `cashRegister` with the closing applied, so
   * a shift the store never saw start (a register left open across the
   * Firestore cutover, or a document removed mid-shift) can still be closed.
   */
  closeAsync(cashRegister: CashRegisterEntity, closing: CashRegisterClosingDetails): Promise<CashRegisterOperationResult>;
  /**
   * Store the register after a device pairing: create it when absent, otherwise
   * merge only the pairing and configuration fields. The operational state
   * (`status`, `opening_details`, `closing_details`) is never taken from the
   * caller's copy, which may be a server record that knows nothing about the
   * shift open on this device.
   */
  savePairedDeviceAsync<T extends CashRegisterEntity>(cashRegister: T): Promise<CashRegisterOperationResult<T>>;
  /**
   * Copy the server-owned configuration onto the stored register. Resolves
   * `undefined` when the register is not stored, so the caller can tell that
   * nothing was written.
   */
  applyServerConfigurationAsync(
    cashRegisterId: string,
    serverCashRegister: CashRegisterEntity
  ): Promise<CashRegisterOperationResult | undefined>;
  /** Detach the register from its user and device and deactivate it (pairing fields only). */
  unpairAsync(cashRegisterId: string): Promise<CashRegisterOperationResult | undefined>;
}

/** Firestore collection holding the latest state of every cash register. */
export const CASH_REGISTERS_COLLECTION = 'cashRegisters';
